<template>
  <div
    ref="editorRef"
    v-ds-tooltip="titleShortcutHint"
    :class="DS_TITLE_FIELD_CLASSES.Editor"
    data-cy="ds-title-field__editor"
    @keydown.esc="handleEscapeKey"
    @blur.capture="handleFocusOut"
    @focus.capture="handleFocusIn"
  >
    <input
      ref="inputRef"
      v-model="draft"
      type="text"
      :class="DS_TITLE_FIELD_CLASSES.Input"
      :placeholder="props.placeholder"
      :maxlength="props.maxlength"
      :readonly="props.loading"
      :aria-label="props.ariaLabel"
      :aria-invalid="props.errorMessageIds ? 'true' : undefined"
      :aria-describedby="props.errorMessageIds"
      data-cy="ds-title-field__input"
      @keydown.enter="handleEnterKey"
    />

    <div :class="DS_TITLE_FIELD_CLASSES.Actions">
      <DSButton
        variant="secondary"
        size="md"
        prefix-icon="cross"
        :aria-label="buttonCancelLabel"
        :disabled="props.loading"
        data-cy="ds-title-field__cancel"
        @mousedown.prevent
        @click.stop="cancelEditing"
      />
      <DSButton
        variant="secondary"
        size="md"
        prefix-icon="check"
        :aria-label="buttonSaveLabel"
        :disabled="props.loading"
        data-cy="ds-title-field__save"
        @mousedown.prevent
        @click.stop="requestSave"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { useDSTitleFieldEditor } from "@/src/design_system/form_elements/title_field/composables/useDSTitleFieldEditor";
import { DS_TITLE_FIELD_CLASSES } from "@/src/design_system/form_elements/title_field/constants/classes";
import type { DSTitleFieldEditorEmits } from "@/src/design_system/form_elements/title_field/types/emits";
import type { DSTitleFieldEditorProps } from "@/src/design_system/form_elements/title_field/types/internal";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";

const props = defineProps<DSTitleFieldEditorProps>();
const emit = defineEmits<DSTitleFieldEditorEmits>();
const editorRef = ref<HTMLElement>();
const inputRef = ref<HTMLInputElement>();
const titleShortcutHint = window.I18n.designSystem.titleField.shortcutHint;
const buttonCancelLabel = window.I18n.buttonCancel;
const buttonSaveLabel = window.I18n.buttonSave;

const {
  draft,
  focus,
  blur,
  containsFocus,
  cancelEditing,
  requestSave,
  handleFocusOut,
  handleFocusIn,
  handleEnterKey,
  handleEscapeKey,
} = useDSTitleFieldEditor({ props, emit, editorRef, inputRef });

defineExpose({ focus, blur, containsFocus });
</script>
